/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useIntl, defineMessages} from 'react-intl'
import {stateOptions} from './state-province-options'
import {useCountry} from '../../hooks'
import {deepMerge} from '../../utils/utils'
import {CheckIcon} from '../icons'
import {
    SALUTATION_OPTIONS,
    COUNTRY_MESSAGES,
    GENERAL_FORM_VALIDATION_MESSAGES as generalMessages
} from '../../constants'
import useGlobalFields from './useGlobalFields'
import {useCurrentCustomer} from '../../hooks/use-current-customer'

const messages = defineMessages({
    required: {defaultMessage: 'Required', id: 'use_address_fields.error.required'},
    phone: {defaultMessage: 'Phone', id: 'use_address_fields.label.phone'},
    phoneHelpText: {
        defaultMessage: 'In the event that we need to reach out to you about your order',
        id: 'use_address_fields.help_text.phone_number'
    },
    country: {defaultMessage: 'Country', id: 'use_address_fields.label.country'},
    address: {defaultMessage: 'Address', id: 'use_address_fields.label.address'},
    city: {defaultMessage: 'City', id: 'use_address_fields.label.city'},
    state: {defaultMessage: 'State', id: 'use_address_fields.label.state'},
    province: {defaultMessage: 'Province', id: 'use_address_fields.label.province'},
    postalCode: {defaultMessage: 'Postal Code', id: 'use_address_fields.label.postal_code'},
    stateCodeInvalid: {
        defaultMessage: 'Please enter 2-letter state/province.',
        id: 'use_address_fields.error.state_code_invalid'
    },
    preferred: {
        defaultMessage: 'Set this address as the primary default shipping address',
        id: 'use_address_fields.label.preferred'
    },
    d2bPreferred: {
        defaultMessage: 'Set this address as my default billing address',
        id: 'use_address_fields.label.d2bPreferred'
    },
    salutation: {defaultMessage: 'Salutation', id: 'use_address_fields.label.salutation'},
    d2bHelptext: {
        defaultMessage:
            'Only the billing address that you used to register your StokkePro account can be used',
        id: 'use_address_fields.label.d2bHelptext'
    }
})

/**
 * A React hook that provides the field definitions for an address form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @param {string} [prefix] - The prefix to be added to the field names
 * @returns {Object} Field definitions for use in a form
 */
export default function useAddressFields({form, prefix = ''}) {
    const {formatMessage, locale} = useIntl()
    const {isD2BCustomer} = useCurrentCustomer().data
    const country = useCountry()
    const {
        control,
        formState: {errors}
    } = form

    const {firstName, lastName, phone} = useGlobalFields({
        form,
        prefix
    })

    // default definition of address fields for B2C customer
    const b2Cfields = {
        salutation: {
            name: `${prefix}salutation`,
            label: formatMessage(messages.salutation),
            defaultValue: 'Mr',
            type: 'radio',
            options: SALUTATION_OPTIONS.map((option) => ({
                value: option.value,
                label: formatMessage(option.label)
            })),
            error: errors[`${prefix}salutation`],
            control
        },
        firstName: {...firstName},
        lastName: {...lastName},
        phone: {
            ...phone,
            helpText: formatMessage(messages.phoneHelpText)
        },
        countryCode: {
            name: `${prefix}countryCode`,
            label: formatMessage(messages.country),
            defaultValue: country,
            type: 'select',
            options: [
                {
                    value: country,
                    label: formatMessage(COUNTRY_MESSAGES[country])
                }
            ],
            // manually mark as required, because there's no required rule as the field is disabled
            isRequired: true,
            inputProps: {
                // country is fixed to the portal
                disabled: true,
                icon: <CheckIcon variant="accepted" />,
                variant: 'locked',
                autoComplete: 'country'
            },
            control
        },
        address1: {
            name: `${prefix}address1`,
            label: formatMessage(messages.address),
            defaultValue: '',
            type: 'text',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your address.',
                    id: 'use_address_fields.error.please_select_your_address'
                }),
                maxLength: {
                    message: formatMessage(generalMessages.maxLength, {
                        label: formatMessage(messages.address),
                        n: 50
                    }),
                    value: 50
                }
            },
            error: errors[`${prefix}address1`],
            control,
            inputProps: {
                autoComplete: 'address-line1'
            }
        },
        address2: {
            name: `${prefix}address2`,
            defaultValue: '',
            type: 'text',
            placeholder: formatMessage({
                defaultMessage: 'Building, appartment...',
                id: 'use_address_fields.error.please_select_your_address2_placeholder'
            }),
            rules: {
                maxLength: {
                    message: formatMessage(generalMessages.maxLength, {
                        label: formatMessage(messages.address),
                        n: 35
                    }),
                    value: 35
                }
            },
            error: errors[`${prefix}address2`],
            control,
            inputProps: {
                autoComplete: 'address-line2',
                'data-testid': 'sf-shipping-address-field-2',
                'aria-label': formatMessage({
                    defaultMessage: 'Additional address information (optional)',
                    id: 'use_address_fields.label.address2_openAddress2'
                })
            }
        },
        city: {
            name: `${prefix}city`,
            label: formatMessage(messages.city),
            defaultValue: '',
            type: 'text',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your city.',
                    id: 'use_address_fields.error.please_select_your_city'
                }),
                minLength: {
                    message: formatMessage(generalMessages.minLength, {
                        label: formatMessage(messages.city),
                        n: 2
                    }),
                    value: 2
                },
                maxLength: {
                    message: formatMessage(generalMessages.maxLength, {
                        label: formatMessage(messages.city),
                        n: 40
                    }),
                    value: 40
                }
            },
            error: errors[`${prefix}city`],
            inputProps: {
                autoComplete: 'address-level2'
            },
            control
        },
        stateCode: {
            name: `${prefix}stateCode`,
            label: formatMessage(messages.state),
            defaultValue: '',
            type: 'select',
            options: [
                {value: '', label: ''},
                ...(stateOptions[country] ? stateOptions[country] : [])
            ],
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select your state.',
                    id: 'use_address_fields.error.please_select_your_state_or_province'
                })
            },
            error: errors[`${prefix}stateCode`],
            inputProps: {
                autoComplete: 'address-level1'
            },
            control
        },
        postalCode: {
            name: `${prefix}postalCode`,
            label: formatMessage(messages.postalCode),
            defaultValue: '',
            type: 'text',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your postal code.',
                    id: 'use_address_fields.error.please_enter_your_postal_or_zip'
                }),
                minLength: {
                    message: formatMessage(generalMessages.minLength, {
                        label: formatMessage(messages.postalCode),
                        n: 4
                    }),
                    value: 4
                },
                maxLength: {
                    message: formatMessage(generalMessages.maxLength, {
                        label: formatMessage(messages.postalCode),
                        n: 4
                    }),
                    value: 4
                },
                pattern: {
                    value: /^\d{4}$/
                }
            },
            error: errors[`${prefix}postalCode`],
            inputProps: {
                autoComplete: 'postal-code'
            },
            control
        },
        preferred: {
            name: `${prefix}preferred`,
            label: formatMessage(messages.preferred),
            defaultValue: false,
            type: 'checkbox',
            rules: {},
            inputProps: {
                size: 'sm'
            },
            control
        }
    }

    // local differences
    // ordered by country code
    const localizedFieldProps = {
        // reset pseude locale
        'en-XB': {
            postalCode: {
                rules: null
            }
        },
        'de-CH': {
            postalCode: {
                rules: {
                    pattern: {
                        value: /^[1-9]\d{3}$/
                    }
                }
            }
        },
        'fr-CH': {
            postalCode: {
                rules: {
                    pattern: {
                        value: /^[1-9]\d{3}$/
                    }
                }
            }
        },
        'it-CH': {
            postalCode: {
                rules: {
                    pattern: {
                        value: /^[1-9]\d{3}$/
                    }
                }
            }
        },
        'en-CZ': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 6
                        }),
                        value: 6
                    },
                    pattern: {
                        value: /^\d{3}\s?\d{2}$/
                    }
                }
            }
        },
        'de-DE': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    pattern: {
                        value: /^\b((?:0[1-46-9]\d{3})|(?:[1-357-9]\d{4})|(?:[4][0-24-9]\d{3})|(?:[6][013-9]\d{3}))\b$/
                    }
                }
            }
        },
        'da-DK': {
            postalCode: {
                rules: {
                    pattern: {
                        value: /^(?:[1-24-9]\d{3}|3[0-8]\d{2})$/
                    }
                }
            }
        },
        'en-CA': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 7
                        }),
                        value: 7
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 7
                        }),
                        value: 7
                    },
                    pattern: {
                        value: /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z] [0-9][ABCEGHJ-NPRSTV-Z][0-9]$/
                    }
                }
            }
        },
        'en-GR': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    pattern: {
                        value: /^\d{5}$/
                    }
                }
            }
        },
        'en-EE': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    pattern: {
                        value: /^\d{5}$/
                    }
                }
            }
        },
        'es-ES': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    pattern: {
                        value: /^([1-9]{2}|[0-9][1-9]|[1-9][0-9])[0-9]{3}$/
                    }
                }
            }
        },
        'en-FI': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    pattern: {
                        value: /^\d{5}$/
                    }
                }
            }
        },
        'fr-FR': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    pattern: {
                        value: /^(F-)?((2[A|B])|[0-9]{2})[0-9]{3}$/
                    }
                }
            }
        },
        'en-GB': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 6
                        }),
                        value: 6
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 9
                        }),
                        value: 9
                    },
                    pattern: {
                        value: /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))\s?[0-9][A-Za-z]{2})$/
                    }
                }
            }
        },
        'en-HR': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    pattern: {
                        value: /^\d{5}$/
                    }
                }
            }
        },
        'en-IE': {
            postalCode: {
                rules: {
                    required: null,
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 10
                        }),
                        value: 10
                    },
                    pattern: {
                        value: /^[AC-FHKNPRTV-Y]{1}[0-9]{1}[0-9W]{1}[ -]?[0-9AC-FHKNPRTV-Y]{4}$/
                    }
                }
            }
        },
        'it-IT': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 7
                        }),
                        value: 7
                    },
                    pattern: {
                        value: /^(V-|I-)?[0-9]{5}$/
                    }
                }
            }
        },
        'ja-JP': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 7
                        }),
                        value: 7
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 8
                        }),
                        value: 8
                    },
                    pattern: {
                        value: /^([0-9]){3}[-]([0-9]){4}$/
                    }
                }
            }
        },
        'en-LT': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 8
                        }),
                        value: 8
                    },
                    pattern: {
                        value: /^([Ll][Tt][- ]{0,1}){0,1}\d{5}$/
                    }
                }
            }
        },
        'en-LV': {
            postalCode: {
                rules: {
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 7
                        }),
                        value: 7
                    },
                    pattern: {
                        value: /^([Ll][Vv][- ]{0,1}){0,1}\d{4}$/
                    }
                }
            }
        },
        'nl-NL': {
            postalCode: {
                rules: {
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 7
                        }),
                        value: 7
                    },
                    pattern: {
                        value: /^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/
                    }
                }
            }
        },
        'pl-PL': {
            postalCode: {
                rules: {
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 6
                        }),
                        value: 6
                    },
                    pattern: {
                        value: /^\d{2}-\d{3}$/
                    }
                }
            }
        },
        'en-PT': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 4
                        }),
                        value: 4
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 8
                        }),
                        value: 8
                    },
                    pattern: {
                        value: /^\d{4}([- ]{0,1}\d{3})?$/
                    }
                }
            }
        },
        'en-RO': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 6
                        }),
                        value: 6
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 6
                        }),
                        value: 6
                    },
                    pattern: {
                        value: /^\d{6}$/
                    }
                }
            }
        },
        'sv-SE': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 6
                        }),
                        value: 6
                    },
                    pattern: {
                        value: /^\d{3}\s?\d{2}$/
                    }
                }
            }
        },
        'en-SI': {
            postalCode: {
                rules: {
                    pattern: {
                        value: /^([Ss][Ii][- ]{0,1}){0,1}\d{4}$/
                    }
                }
            }
        },
        'en-SK': {
            postalCode: {
                rules: {
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 6
                        }),
                        value: 6
                    },
                    pattern: {
                        value: /^\d{3}\s?\d{2}$/
                    }
                }
            }
        },
        'en-US': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 4
                        }),
                        value: 4
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 10
                        }),
                        value: 10
                    },
                    pattern: {
                        value: /^(^\d{4,5}(-\d{4,5})?$)|(^[abceghjklmnprstvxyABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Za-z]{1} *\d{1}[A-Za-z]{1}\d{1}$)$/
                    }
                }
            }
        },
        'ko-KR': {
            postalCode: {
                rules: {
                    minLength: {
                        message: formatMessage(generalMessages.minLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    maxLength: {
                        message: formatMessage(generalMessages.maxLength, {
                            label: formatMessage(messages.postalCode),
                            n: 5
                        }),
                        value: 5
                    },
                    pattern: {
                        value: /^\d{5}$/
                    }
                }
            }
        }
    }
    const {salutation, ...rest} = b2Cfields

    const fields = !isD2BCustomer
        ? b2Cfields
        : {
              ...rest,
              preferred: {
                  ...rest.preferred,
                  label: formatMessage(messages.d2bPreferred),
                  isDisabled: true,
                  helpText: formatMessage(messages.d2bHelptext)
              }
          }

    deepMerge(fields, localizedFieldProps[locale])

    return fields
}
